import { useEffect } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { Link, LinkProps, useHistory } from 'react-router-dom';
import { Button, Flex } from 'yoso';
import { useLogout } from '~/components/Auth';
import { ErrorPage } from '~/components/Error/ErrorPage';
import { MaintenanceErrorMessage } from '~/components/Error/Message/Maintenance/MaintenanceErrorMessage';
import { PartialMaintenanceErrorMessage } from '~/components/Error/Message/Maintenance/PartialMaintenanceErrorMessage';
import { RevokedErrorMessage } from '~/components/Error/Message/Revoked/RevokedErrorMessage';
import { ErrorMessage } from '~/components/Error/Message/v2/ErrorMessage';
import { t } from '~/components/I18n';
import { BaseLayout } from '~/components/Layout';
import { IsolatedHeader } from '~/components/Layout/Header/Isolated/IsolatedHeader';

export type ApplicationErrorFallbackProps = FallbackProps;
export const ApplicationErrorFallback = ({
  error,
  resetErrorBoundary,
}: ApplicationErrorFallbackProps) => {
  const history = useHistory();
  const logout = useLogout();

  useEffect(
    () =>
      history.listen(() => {
        resetErrorBoundary();
      }),
    [history, resetErrorBoundary]
  );

  const pageName = error.message.includes('MAINTENANCE') ? 'Maintenance' : 'Error';

  return (
    <BaseLayout>
      <div className="base-layout-header" data-event-category="gnb">
        <IsolatedHeader />
      </div>
      <Flex flex={1}>
        <ErrorPage
          data-event="pageview"
          data-event-page_name={pageName}
          data-event-error_code={error.message}>
          {(() => {
            switch (error.message) {
              case 'UNAUTHORIZED':
                return (
                  <ErrorMessage
                    title={t('citizen_error_access_title')}
                    description={t('citizen_error_unauthorized_desc')}>
                    <Button color="primary" size="large" onClick={logout}>
                      {t('citizen_homebutton')}
                    </Button>
                  </ErrorMessage>
                );
              case 'ACCESS_TOKEN_IS_REVOKED':
                return (
                  <RevokedErrorMessage
                    title={t('citizen_error_loginexpired_title')}
                    description={t('citizen_error_loginexpired_desc')}
                  />
                );
              case 'NOT_FOUND':
                return (
                  <ErrorMessage
                    title={t('citizen_error_notfound_title')}
                    description={t('citizen_error_notfound_desc')}>
                    <Button<LinkProps> as={Link} to="/" size="large" color="primary">
                      {t('citizen_homebutton')}
                    </Button>
                  </ErrorMessage>
                );
              case 'MAINTENANCE':
                return <MaintenanceErrorMessage error={error} />;
              case 'PARTIAL_MAINTENANCE':
                return <PartialMaintenanceErrorMessage error={error} />;
              case 'JP_SIGNUP_ERROR':
                return (
                  <ErrorMessage
                    title={t('citizen_error_terms_title')}
                    description={t('citizen_error_jp_account_desc')}>
                    <Button color="primary" size="large" onClick={logout}>
                      {t('citizen_homebutton')}
                    </Button>
                  </ErrorMessage>
                );
              case 'USER_REGION_ERROR':
                return (
                  <ErrorMessage
                    title={t('citizen_error_terms_title')}
                    description={t('citizen_error_region_desc')}>
                    <Button as="a" href="/" size="large" color="primary">
                      {t('citizen_homebutton')}
                    </Button>
                  </ErrorMessage>
                );
              default:
                return (
                  <ErrorMessage
                    title={t('citizen_error_etc_title')}
                    description={t('citizen_error_etc_desc')}>
                    <Button as="a" href="/" size="large" color="primary">
                      {t('citizen_homebutton')}
                    </Button>
                  </ErrorMessage>
                );
            }
          })()}
        </ErrorPage>
      </Flex>
    </BaseLayout>
  );
};
